import React, { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Avatar,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ElectricalServices,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Logo from '@/assets/InoelecLogo1.png';
import { useForm } from 'react-hook-form';

// Styled components con diseño empresarial
const LoginContainer = styled(Container)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundImage: 'none',
}));

const LoginCard = styled(Card)(() => ({
  maxWidth: 440,
  width: '100%',
  borderRadius: 12,
}));

const LogoContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: 32,
  paddingTop: 8,
}));

const StyledAvatar = styled(Avatar)(() => ({
  width: 64,
  height: 64,
  marginBottom: 16,
  border: 'none',
  boxShadow: 'none',
}));

const LoginForm = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    transition: 'all 0.2s ease',
    '&:hover': {},
    '&.Mui-focused': {},
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
  '& .MuiInputLabel-root.Mui-focused': {},
}));

const StyledButton = styled(Button)(() => ({
  borderRadius: 8,
  padding: '12px 24px',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  boxShadow: 'none',
  transition: 'all 0.2s ease',
  '&:hover': {},
  '&:active': {},
  '&:disabled': {},
}));

const CompanyInfo = styled(Box)(() => ({
  textAlign: 'center',
  marginTop: 24,
  padding: 16,
  borderRadius: 8,
}));

const HeaderSection = styled(Box)(() => ({
  textAlign: 'center',
  marginBottom: 24,
  paddingBottom: 16,
}));

interface LoginFormInputs {
  email: string;
  password: string;
}

export const Login: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const login = useLogin();
  const notify = useNotify();
  const { register, handleSubmit, formState } = useForm<LoginFormInputs>();

  const handleLogin = async (data: LoginFormInputs) => {
    setLoading(true);
    try {
      await login({ username: data.email, password: data.password });
    } catch (error) {
      notify('Error de autenticación. Verifique sus credenciales.', {
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer maxWidth={false}>
      <LoginCard elevation={0}>
        <CardContent sx={{ p: 5 }}>
          <LogoContainer>
            <img src={Logo} alt='Inoelec Logo' />
          </LogoContainer>

          <HeaderSection>
            <Typography variant='body2'>
              Acceda a su panel de administración
            </Typography>
          </HeaderSection>

          <Divider sx={{ mb: 3 }} />

          <LoginForm
            onSubmit={handleSubmit(handleLogin)}
            aria-label='Formulario de login'
          >
            <StyledTextField
              fullWidth
              label='Usuario'
              variant='outlined'
              disabled={loading}
              placeholder='Ingrese su usuario'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <PersonIcon />
                  </InputAdornment>
                ),
              }}
              {...register('email', { required: true })}
              aria-label='Correo electrónico'
            />

            <StyledTextField
              fullWidth
              label='Contraseña'
              type={showPassword ? 'text' : 'password'}
              variant='outlined'
              disabled={loading}
              placeholder='Ingrese su contraseña'
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <LockIcon sx={{}} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge='end'
                      disabled={loading}
                      sx={{}}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...register('password', { required: true })}
              aria-label='Contraseña'
            />
            <StyledButton
              type='submit'
              fullWidth
              variant='contained'
              size='large'
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color='inherit' />
                  <span>Iniciando sesión...</span>
                </Box>
              ) : (
                'Iniciar Sesión'
              )}
            </StyledButton>
          </LoginForm>

          <CompanyInfo>
            <Typography variant='body2' sx={{ fontWeight: 500 }}>
              © 2025 Inoelec
            </Typography>
            <Typography variant='caption' sx={{}}>
              Sistema de Gestión Industrial - Versión beta 1.0
            </Typography>
          </CompanyInfo>
        </CardContent>
      </LoginCard>
    </LoginContainer>
  );
};

export default React.memo(Login);
